import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  shorthands,
  Text,
  Button,
  Card,
  CardHeader,
  Badge,
  ProgressBar,
  Spinner,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Accordion,
  InfoButton,
  Tooltip,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '../Common/ScrollableMessageBar';
import {
  Lightbulb24Regular,
  Warning24Regular,
  CheckmarkCircle24Regular,
  ArrowSplit24Regular,
  Filter24Regular,
  Organization24Regular,
  ChevronRight24Regular,
  Info24Regular,
} from '@fluentui/react-icons';
import { gfcPolicyService, DomainLimitOptimization } from '../../services/gfcPolicyService';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  optimizationCard: {
    ...shorthands.margin('16px', '0'),
  },
  analysisHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.margin('0', '0', '16px', '0'),
  },
  domainCount: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    ...shorthands.margin('16px', '0'),
  },
  strategiesContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  strategyCard: {
    ...shorthands.padding('16px'),
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
    ...shorthands.borderRadius('8px'),
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      ...shorthands.border('1px', 'solid', '#106ebe'),
    },
  },
  recommendedStrategy: {
    ...shorthands.border('1px', 'solid', '#107C10'),
    backgroundColor: '#f3f9fc',
  },
  strategyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.margin('0', '0', '12px', '0'),
  },
  strategyTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  impactBadge: {
    minWidth: '80px',
  },
  splitPoliciesTable: {
    ...shorthands.margin('16px', '0'),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('24px'),
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    ...shorthands.gap('16px'),
    ...shorthands.margin('16px', '0'),
  },
  summaryItem: {
    textAlign: 'center',
    ...shorthands.padding('12px'),
    backgroundColor: '#f8f9fa',
    ...shorthands.borderRadius('6px'),
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
  },
});

interface DomainOptimizerProps {
  domains: string[];
  onOptimizationApply?: (optimization: DomainLimitOptimization) => void;
  autoOptimize?: boolean;
  disabled?: boolean;
}

export const DomainOptimizer: React.FC<DomainOptimizerProps> = ({
  domains,
  onOptimizationApply,
  autoOptimize = false,
  disabled = false,
}) => {
  const styles = useStyles();
  const [loading, setLoading] = useState(false);
  const [optimization, setOptimization] = useState<DomainLimitOptimization | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedStrategies, setExpandedStrategies] = useState<string[]>([]);

  const analyzeOptimization = async () => {
    if (domains.length === 0) {
      setOptimization(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await gfcPolicyService.optimizeDomains(domains);
      setOptimization(result.optimization);

      // Auto-expand recommended strategy
      if (result.optimization.recommendedStrategy) {
        setExpandedStrategies([result.optimization.recommendedStrategy]);
      }
    } catch (err) {
      console.error('Failed to analyze domain optimization:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze domain optimization');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (domains.length > 0 && (autoOptimize || domains.length > 80)) {
      analyzeOptimization();
    }
  }, [domains, autoOptimize]);

  const getStrategyIcon = (strategy: string) => {
    switch (strategy) {
      case 'split_policies':
        return <ArrowSplit24Regular style={{ color: '#106ebe' }} />;
      case 'remove_duplicates':
        return <Filter24Regular style={{ color: '#107C10' }} />;
      case 'consolidate_wildcards':
        return <Organization24Regular style={{ color: '#ff8c00' }} />;
      case 'risk_based_filtering':
        return <Warning24Regular style={{ color: '#d13438' }} />;
      default:
        return <Lightbulb24Regular style={{ color: '#605e5c' }} />;
    }
  };

  const getImpactBadge = (domainReduction: number) => {
    if (domainReduction <= 0) {
      return <Badge appearance="outline" color="subtle" data-testid="gfc-domain-optimizer-impact-none">No Impact</Badge>;
    } else if (domainReduction < 10) {
      return <Badge appearance="filled" color="success" data-testid="gfc-domain-optimizer-impact-low">Low Impact</Badge>;
    } else if (domainReduction < 25) {
      return <Badge appearance="filled" color="warning" data-testid="gfc-domain-optimizer-impact-medium">Medium Impact</Badge>;
    } else {
      return <Badge appearance="filled" color="important" data-testid="gfc-domain-optimizer-impact-high">High Impact</Badge>;
    }
  };

  const getProgressColor = (current: number, max: number) => {
    const percentage = (current / max) * 100;
    if (percentage <= 70) return 'brand';
    if (percentage <= 90) return 'warning';
    return 'error';
  };

  const toggleStrategyExpansion = (strategy: string) => {
    setExpandedStrategies(prev =>
      prev.includes(strategy)
        ? prev.filter(s => s !== strategy)
        : [...prev, strategy]
    );
  };

  const handleApplyOptimization = () => {
    if (optimization && onOptimizationApply) {
      onOptimizationApply(optimization);
    }
  };

  // Don't show optimizer if domains are within limits and not auto-optimizing
  if (domains.length <= 80 && !autoOptimize && !optimization) {
    return null;
  }

  if (loading) {
    return (
      <Card className={styles.optimizationCard} data-testid="gfc-domain-optimizer-loading">
        <div className={styles.loadingContainer} data-testid="gfc-domain-optimizer-loading-container">
          <Spinner size="large" data-testid="gfc-domain-optimizer-loading-spinner" />
          <Text data-testid="gfc-domain-optimizer-loading-text">Analyzing domain optimization strategies...</Text>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={styles.optimizationCard} data-testid="gfc-domain-optimizer-error">
        <CardHeader
          header={
            <div className={styles.analysisHeader} data-testid="gfc-domain-optimizer-error-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="gfc-domain-optimizer-error-title-row">
                <Warning24Regular style={{ color: '#d13438' }} />
                <Text weight="semibold" size={500} data-testid="gfc-domain-optimizer-error-title">Domain Optimization</Text>
              </div>
            </div>
          }
        />
        <div style={{ padding: '0 16px 16px' }} data-testid="gfc-domain-optimizer-error-content">
          <MessageBar intent="error" data-testid="gfc-domain-optimizer-error-bar">
            <MessageBarBody data-testid="gfc-domain-optimizer-error-body">
              <Text weight="semibold" data-testid="gfc-domain-optimizer-error-heading">Optimization Analysis Failed</Text>
              <br data-testid="gfc-domain-optimizer-error-break" />
              <Text data-testid="gfc-domain-optimizer-error-text">{error}</Text>
            </MessageBarBody>
          </MessageBar>
          <Button 
            onClick={analyzeOptimization}
            style={{ marginTop: '12px' }}
            data-testid="gfc-domain-optimizer-retry"
          >
            Retry Analysis
          </Button>
        </div>
      </Card>
    );
  }

  if (!optimization) {
    return (
      <Card className={styles.optimizationCard} data-testid="gfc-domain-optimizer-initial">
        <CardHeader
          header={
            <div className={styles.analysisHeader} data-testid="gfc-domain-optimizer-initial-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="gfc-domain-optimizer-initial-title-row">
                <Lightbulb24Regular style={{ color: '#106ebe' }} />
                <Text weight="semibold" size={500} data-testid="gfc-domain-optimizer-initial-title">Domain Optimization</Text>
                <InfoButton
                  info={
                    <div style={{ maxWidth: '300px' }} data-testid="gfc-domain-optimizer-info-content">
                      <Text size={200} data-testid="gfc-domain-optimizer-info-text">
                        Analyze your domain list for optimization opportunities. This helps ensure 
                        your policy meets GFC limits and follows best practices.
                      </Text>
                    </div>
                  }
                />
              </div>
              <Button
                appearance="primary"
                onClick={analyzeOptimization}
                disabled={disabled || domains.length === 0}
                data-testid="gfc-domain-optimizer-analyze"
              >
                Analyze Domains
              </Button>
            </div>
          }
        />
        <div style={{ padding: '0 16px 16px' }} data-testid="gfc-domain-optimizer-initial-content">
          <Text size={300} style={{ color: '#605e5c' }} data-testid="gfc-domain-optimizer-initial-description">
            {domains.length === 0 
              ? 'Add domains to analyze optimization opportunities'
              : `Ready to analyze ${domains.length} domains for optimization opportunities`
            }
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.optimizationCard} data-testid="gfc-domain-optimizer-results">
      <CardHeader
        header={
          <div className={styles.analysisHeader} data-testid="gfc-domain-optimizer-results-header">
            <div className={styles.domainCount} data-testid="gfc-domain-optimizer-domain-count-row">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="gfc-domain-optimizer-results-title-row">
                {optimization.exceedsLimit ? (
                  <Warning24Regular style={{ color: '#d13438' }} />
                ) : (
                  <CheckmarkCircle24Regular style={{ color: '#107C10' }} />
                )}
                <Text weight="semibold" size={500} data-testid="gfc-domain-optimizer-results-title">Domain Analysis Results</Text>
              </div>
              <Badge 
                appearance="filled" 
                color={optimization.exceedsLimit ? 'danger' : 'success'}
                data-testid="gfc-domain-optimizer-results-domain-count-badge"
              >
                {optimization.originalDomainCount} domains
              </Badge>
            </div>
            <Button
              appearance="subtle"
              onClick={analyzeOptimization}
              disabled={disabled || loading}
              data-testid="gfc-domain-optimizer-reanalyze"
            >
              Re-analyze
            </Button>
          </div>
        }
      />

      <div style={{ padding: '0 16px 16px' }} data-testid="gfc-domain-optimizer-results-content">
        {/* Domain Limit Progress */}
        <div className={styles.progressContainer} data-testid="gfc-domain-optimizer-progress-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} data-testid="gfc-domain-optimizer-progress-header">
            <Text weight="medium" size={400} data-testid="gfc-domain-optimizer-progress-title">
              GFC Domain Limit Usage
            </Text>
            <Text size={300} data-testid="gfc-domain-optimizer-progress-count">
              {optimization.originalDomainCount} / 100 domains
            </Text>
          </div>
          <ProgressBar
            value={Math.min((optimization.originalDomainCount / 100) * 100, 100)}
            color={getProgressColor(optimization.originalDomainCount, 100)}
            data-testid="gfc-domain-optimizer-progress-bar"
          />
        </div>

        {/* Summary Grid */}
        <div className={styles.summaryGrid} data-testid="gfc-domain-optimizer-summary-grid">
          <div className={styles.summaryItem} data-testid="gfc-domain-optimizer-summary-original">
            <Text size={700} weight="bold" style={{ color: '#106ebe', display: 'block' }} data-testid="gfc-domain-optimizer-summary-original-value">
              {optimization.originalDomainCount}
            </Text>
            <Text size={300} data-testid="gfc-domain-optimizer-summary-original-label">Original Count</Text>
          </div>
          <div className={styles.summaryItem} data-testid="gfc-domain-optimizer-summary-limit-status">
            <Text size={700} weight="bold" style={{ color: optimization.exceedsLimit ? '#d13438' : '#107C10', display: 'block' }} data-testid="gfc-domain-optimizer-summary-limit-value">
              {optimization.exceedsLimit ? 'Over' : 'Within'}
            </Text>
            <Text size={300} data-testid="gfc-domain-optimizer-summary-limit-label">Limit Status</Text>
          </div>
          <div className={styles.summaryItem} data-testid="gfc-domain-optimizer-summary-strategies">
            <Text size={700} weight="bold" style={{ color: '#ff8c00', display: 'block' }} data-testid="gfc-domain-optimizer-summary-strategies-value">
              {optimization.strategies.length}
            </Text>
            <Text size={300} data-testid="gfc-domain-optimizer-summary-strategies-label">Strategies</Text>
          </div>
          <div className={styles.summaryItem} data-testid="gfc-domain-optimizer-summary-optimized">
            <Text size={700} weight="bold" style={{ color: '#107C10', display: 'block' }} data-testid="gfc-domain-optimizer-summary-optimized-value">
              {optimization.optimizedDomainCount}
            </Text>
            <Text size={300} data-testid="gfc-domain-optimizer-summary-optimized-label">Optimized Count</Text>
          </div>
        </div>

        {/* Status Message */}
        {optimization.exceedsLimit ? (
          <MessageBar intent="warning" data-testid="gfc-domain-optimizer-limit-warning-bar">
            <MessageBarBody data-testid="gfc-domain-optimizer-limit-warning-body">
              <Text weight="semibold" data-testid="gfc-domain-optimizer-limit-warning-title">Domain Limit Exceeded</Text>
              <br data-testid="gfc-domain-optimizer-limit-warning-break" />
              <Text data-testid="gfc-domain-optimizer-limit-warning-text">
                Your domain list exceeds the GFC limit of 100 domains per policy. 
                Review the optimization strategies below to reduce your domain count or split into multiple policies.
              </Text>
            </MessageBarBody>
          </MessageBar>
        ) : (
          <MessageBar intent="success" data-testid="gfc-domain-optimizer-success-bar">
            <MessageBarBody data-testid="gfc-domain-optimizer-success-body">
              <Text weight="semibold" data-testid="gfc-domain-optimizer-success-title">Within GFC Limits</Text>
              <br data-testid="gfc-domain-optimizer-success-break" />
              <Text data-testid="gfc-domain-optimizer-success-text">
                Your domain list meets GFC requirements. The strategies below can help further optimize your configuration.
              </Text>
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Optimization Strategies */}
        {optimization.strategies.length > 0 && (
          <div className={styles.strategiesContainer} data-testid="gfc-domain-optimizer-strategies-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }} data-testid="gfc-domain-optimizer-strategies-header">
              <Text weight="semibold" size={400} data-testid="gfc-domain-optimizer-strategies-title">Optimization Strategies</Text>
              <InfoButton
                info={
                  <div style={{ maxWidth: '300px' }} data-testid="gfc-domain-optimizer-strategies-info-content">
                    <Text size={200} data-testid="gfc-domain-optimizer-strategies-info-text">
                      These strategies can help reduce domain count and improve policy performance. 
                      The recommended strategy is highlighted based on your specific configuration.
                    </Text>
                  </div>
                }
              />
            </div>

            <Accordion collapsible multiple>
              {optimization.strategies.map((strategy, index) => {
                const isRecommended = strategy.strategy === optimization.recommendedStrategy;
                
                return (
                  <AccordionItem key={strategy.strategy} value={strategy.strategy}>
                    <AccordionHeader>
                      <div className={styles.strategyHeader} style={{ width: '100%' }} data-testid={`gfc-domain-optimizer-strategy-header-${index}`}>
                        <div className={styles.strategyTitle} data-testid={`gfc-domain-optimizer-strategy-title-${index}`}>
                          {getStrategyIcon(strategy.strategy)}
                          <Text weight="medium" size={400} data-testid={`gfc-domain-optimizer-strategy-description-${index}`}>
                            {strategy.description}
                          </Text>
                          {isRecommended && (
                            <Badge appearance="filled" color="success" data-testid={`gfc-domain-optimizer-strategy-recommended-${index}`}>
                              Recommended
                            </Badge>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid={`gfc-domain-optimizer-strategy-impact-${index}`}>
                          {getImpactBadge(strategy.domainReduction)}
                          <Text size={300} data-testid={`gfc-domain-optimizer-strategy-reduction-${index}`}>
                            -{strategy.domainReduction} domains
                          </Text>
                        </div>
                      </div>
                    </AccordionHeader>
                    <AccordionPanel>
                      <div style={{ padding: '8px 0' }} data-testid={`gfc-domain-optimizer-strategy-panel-${index}`}>
                        {strategy.strategy === 'split_policies' && optimization.splitPolicies && (
                          <div data-testid="gfc-domain-optimizer-split-policies-section">
                            <Text size={300} style={{ marginBottom: '12px', display: 'block' }} data-testid="gfc-domain-optimizer-split-policies-description">
                              This strategy will create {optimization.splitPolicies.length} separate policies:
                            </Text>
                            <Table className={styles.splitPoliciesTable} data-testid="gfc-domain-optimizer-split-policies-table">
                              <TableHeader data-testid="gfc-domain-optimizer-split-policies-table-header">
                                <TableRow data-testid="gfc-domain-optimizer-split-policies-header-row">
                                  <TableHeaderCell data-testid="gfc-domain-optimizer-split-policies-header-policy-name">Policy Name</TableHeaderCell>
                                  <TableHeaderCell data-testid="gfc-domain-optimizer-split-policies-header-domain-count">Domain Count</TableHeaderCell>
                                  <TableHeaderCell data-testid="gfc-domain-optimizer-split-policies-header-status">Status</TableHeaderCell>
                                </TableRow>
                              </TableHeader>
                              <TableBody data-testid="gfc-domain-optimizer-split-policies-table-body">
                                {optimization.splitPolicies.map((policy, idx) => (
                                  <TableRow key={idx} data-testid={`gfc-domain-optimizer-split-policy-row-${idx}`}>
                                    <TableCell data-testid={`gfc-domain-optimizer-split-policy-name-cell-${idx}`}>
                                      <Text className={styles.domainCount} data-testid={`gfc-domain-optimizer-split-policy-name-${idx}`}>{policy.policyName}</Text>
                                    </TableCell>
                                    <TableCell data-testid={`gfc-domain-optimizer-split-policy-domain-count-cell-${idx}`}>
                                      <Text data-testid={`gfc-domain-optimizer-split-policy-domain-count-${idx}`}>{policy.domainCount}</Text>
                                    </TableCell>
                                    <TableCell data-testid={`gfc-domain-optimizer-split-policy-status-cell-${idx}`}>
                                      <Badge 
                                        appearance="outline" 
                                        color={policy.domainCount <= 100 ? 'success' : 'danger'}
                                        data-testid={`gfc-domain-optimizer-split-policy-status-${idx}`}
                                      >
                                        {policy.domainCount <= 100 ? 'Within Limits' : 'Over Limit'}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}

                        {strategy.strategy === 'remove_duplicates' && (
                          <Text size={300} data-testid="gfc-domain-optimizer-strategy-remove-duplicates-text">
                            Remove duplicate domain entries and normalize domain formatting to reduce the total count.
                          </Text>
                        )}

                        {strategy.strategy === 'consolidate_wildcards' && (
                          <Text size={300} data-testid="gfc-domain-optimizer-strategy-consolidate-wildcards-text">
                            Replace multiple subdomain entries with wildcard patterns where appropriate (e.g., replace multiple *.contoso.com entries with a single wildcard).
                          </Text>
                        )}

                        {strategy.strategy === 'risk_based_filtering' && (
                          <Text size={300} data-testid="gfc-domain-optimizer-strategy-risk-based-filtering-text">
                            Remove domains that don't meet your risk criteria or move high-risk domains to a separate policy for enhanced monitoring.
                          </Text>
                        )}
                      </div>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}

        {/* Apply Optimization */}
        {onOptimizationApply && optimization.strategies.length > 0 && (
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }} data-testid="gfc-domain-optimizer-actions">
            <Button
              appearance="primary"
              onClick={handleApplyOptimization}
              disabled={disabled}
              data-testid="gfc-domain-optimizer-apply-strategy"
            >
              Apply {optimization.recommendedStrategy ? 'Recommended' : 'Best'} Strategy
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setOptimization(null)}
              disabled={disabled}
              data-testid="gfc-domain-optimizer-close-analysis"
            >
              Close Analysis
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};